// A lightweight excitation model, not a measured sample or complete waveguide.
// Runs before the instrument's pressure filter and body response. Stable seeded
// noise makes renders repeatable, independent of audio callback block sizes.
import {spectralModes} from "./SpectralVoice.js";
import {vocalModes} from "./VocalVoice.js";
export const acousticModes: readonly string[] = ["none", "bow", "reed", "air", "pluck", "bowed", ...spectralModes, ...vocalModes.map(m => m.name)];
/** Mode index ranges: legacy excitations, measured spectral voices, sung voices. */
export const firstSpectralMode = 6, firstVocalMode = 6 + spectralModes.length;
export class AcousticVoice {
    private seed: number = 1;
    private lowNoise: number = 0;
    private highNoise: number = 0;
    private body: number = 0;
    private energy: number = 0;
    private motion: number = 0;
    private target: number = 0;
    private countdown: number = 0;
    private attack: number = 1;
    private noiseLow: number = 0;
    private noiseHigh: number = 0;
    private colorRate: number = 0;
    private motionRate: number = 0;
    private energyRate: number = 0;
    private attackDecay: number = 0;
    private interval: number = 1;
    private texture: number = 0;
    private depth: number = 0;
    private mode: number = 0;
    // Independent, bounded contact-pressure movement across body bands. Fixed
    // Hz bands model body coloration, not a pitch LFO. The legacy bow mode is
    // intentionally unchanged so previously saved scores retain their sound.
    private bands = [450,1500,3200].map(hz=>({hz,b0:0,a1:0,a2:0,x1:0,x2:0,y1:0,y2:0,motion:0,target:0}));
    private bandSeed: number = 1;
    private bandRate: number = 0;
    public reset(seed: number = 1): void {
        this.seed = (seed | 0) || 1;
        this.lowNoise=this.highNoise=this.body=this.energy=this.motion=this.target=0;
        this.countdown=0;this.attack=1;this.bandSeed=((seed|0)^0x5bd1e995)||1;
        for(const b of this.bands)b.x1=b.x2=b.y1=b.y2=b.motion=b.target=0;
    }
    public configure(mode: number, texture: number, motion: number, rate: number, frequency: number): void {
        this.mode=mode;this.texture=texture/100;this.depth=motion/100;
        const coefficient=(hz:number):number=>1-Math.exp(-2*Math.PI*Math.min(hz,rate*.4)/rate);
        this.noiseLow=coefficient(mode===3?7500:mode===4?9000:(mode===1||mode===5)?5500:4500);
        this.noiseHigh=coefficient(mode===3?600:mode===4?1400:(mode===1||mode===5)?450:850);
        this.colorRate=coefficient(Math.max(650,frequency*(mode===2?2.5:2)));
        this.motionRate=coefficient(3);this.energyRate=coefficient(80);
        this.attackDecay=Math.exp(-1/(rate*((mode===1||mode===5)?.075:mode===3?.09:mode===4?.012:.035)));
        this.bandRate=coefficient(5);
        if(mode===5)for(const b of this.bands){
            const w=2*Math.PI*Math.min(b.hz,rate*.35)/rate,alpha=Math.sin(w)/(2*1.1),a0=1+alpha;
            b.b0=alpha/a0;b.a1=-2*Math.cos(w)/a0;b.a2=(1-alpha)/a0;
        }
        this.interval=Math.max(1,Math.round(rate*.083));
    }
    public process(input: number): number {
        let x=this.seed;x^=x<<13;x^=x>>>17;x^=x<<5;this.seed=x;
        const random=(x>>>0)/2147483648-1;
        if(--this.countdown<=0){
            this.target=random;this.countdown=this.interval;
            if(this.mode===5)for(const b of this.bands){
                let r=this.bandSeed;r^=r<<13;r^=r>>>17;r^=r<<5;this.bandSeed=r;b.target=(r>>>0)/2147483648-1;
            }
        }
        this.motion+=this.motionRate*(this.target-this.motion);
        this.lowNoise+=this.noiseLow*(random-this.lowNoise);
        this.highNoise+=this.noiseHigh*(random-this.highNoise);
        this.body+=this.colorRate*(input-this.body);
        this.energy+=this.energyRate*(Math.abs(input)-this.energy);
        // Bow friction continues, reed turbulence is mostly an onset cue, air
        // remains audible under a flute tone; contact noise on plucks dies away.
        const sustain=(this.mode===1||this.mode===5)?.35:this.mode===2?.12:this.mode===3?.7:0;
        const gesture=sustain+(1-sustain)*this.attack;
        // Air-jet upper partials settle in after the breath onset; bow/reed
        // contact starts with an edge, then settles into a less static sustain.
        const color=this.depth*(this.attack*(this.mode===3?-.45:.4)+(this.mode===4?0:this.motion*.8));
        const noise=(this.lowNoise-this.highNoise)*this.energy*this.texture*gesture*(this.mode===3?.5:.3);
        let output=input+(input-this.body)*color+noise;
        if(this.mode===5)for(const b of this.bands){
            b.motion+=this.bandRate*(b.target-b.motion);
            const y=b.b0*(input-b.x2)-b.a1*b.y1-b.a2*b.y2;
            b.x2=b.x1;b.x1=input;b.y2=b.y1;b.y1=y;
            output+=y*this.depth*1.2*b.motion;
        }
        this.attack*=this.attackDecay;
        return output;
    }
}
